import { getStudioPlanRank } from 'src/routes/settings/plans/components/studio/studioFeatureGating';
import type {
  SellableStudioPlanId,
  StudioBillingInterval,
} from 'src/routes/settings/plans/components/studio/studioPlanFeatures';
import type {
  ExtraAppPrice,
  StudioAppUsageResponse,
  StudioPlan,
  StudioPlanId,
  StudioPlanTier,
} from 'src/services/api/studioCreditApi';

/**
 * The app-allowance rules the client still owns. The allowance, the current
 * count and the per-app fee are all the server's (`/studio-app-usage`,
 * `/studio-extra-apps-preview`) — the same count billing meters against — so
 * what is left here is the upgrade ladder, which has no server equivalent.
 *
 * Kept free of React, redux and API imports so it stays unit-testable on its
 * own — `usePlanStatus` reaches `src/utils` and therefore `axios`, which needs
 * `window`.
 */

/**
 * The single way past the limit this plan has. `combined` is Free whose
 * allowance already matches a paid plan's: upgrading alone frees no slot, so the
 * plan and the app fee are sold together.
 */
export type AppLimitRoute = 'fee' | 'upgrade' | 'combined';

/**
 * How many extra apps a paid plan would bill once the interrupted app lands:
 * the live count plus that app, past the paid allowance. The same sum the
 * server sells in the combined checkout.
 */
export const getExtraAppsAfterUpgrade = (countableApps: number): number =>
  Math.max(countableApps + 1 - PAID_PLAN_INCLUDED_APPS, 0);

/**
 * Picks the route from the plan and the live count the server reports, never
 * the onboarding flag: what decides it is whether an upgrade alone makes room.
 * Counting rather than comparing allowances also covers a Free workspace that
 * is already past its cap.
 */
export const getAppLimitRoute = (
  studioPlan: StudioPlanId,
  countableApps: number,
): AppLimitRoute => {
  if (studioPlan !== 'freemium') {
    return 'fee';
  }
  return getExtraAppsAfterUpgrade(countableApps) > 0 ? 'combined' : 'upgrade';
};

/** Which action the limit interrupted; decides the copy's verb. */
export type AppLimitSurface = 'publish' | 'add';

/**
 * Whether the workspace has run out of room for one more app. Only `studio`
 * workspaces carry an allowance: Classic and unsubscribed ones are exempt, and an
 * unresolved read fails OPEN, because wrongly blocking is far worse than wrongly
 * allowing — the server enforces the same allowance regardless.
 *
 * Both surfaces that can reach the limit ask exactly this, because neither's app
 * is in `countableApps` yet: a marketplace app being added does not exist, and a
 * build is discounted from the server's count until it publishes. Building is
 * never gated — an app is charged against the allowance when it goes live.
 *
 * `appId` is the app about to be added. One the catalog excludes from the
 * count (the Home App) never spends a slot, so it is let through at the limit
 * — the same call the server makes, which would accept the install anyway.
 *
 * An extra app the subscription already bills is room too: the combined
 * checkout sells the app before it lands, and the server would answer that
 * app's purchase as already paid.
 */
export const isUsageAtLimit = (
  usage: StudioAppUsageResponse | undefined,
  appId?: string,
): boolean =>
  usage?.family === 'studio' &&
  !usage.unlimitedApps &&
  !(appId && usage.excludedAppIds?.includes(appId)) &&
  usage.countableApps >= usage.includedApps + (usage.paidExtraApps ?? 0);

/**
 * Whether a Freemium workspace is already over its allowance — not merely at
 * it. Distinct from {@link isUsageAtLimit} (`>=`), which intercepts a new add.
 *
 * At-limit (exactly 3/3) is compliant Freemium usage and must not lock
 * already-installed extensions. Over-limit is reachable when
 * `EnableStudioPricing` flips on for a portal that accumulated apps while the
 * flag was off. Paid Studio plans buy extras past the cap, so over-count there
 * is not a lock. Classic / unresolved (`studioPlan` null) carry no Freemium
 * allowance.
 */
export const isFreemiumOverAppLimit = (
  studioPlan: StudioPlanId | null,
  countableApps: number,
  includedApps: number,
): boolean => studioPlan === 'freemium' && countableApps > includedApps;

/**
 * Already-installed extensions lock only when Freemium is over the cap and the
 * item is appId-backed and countable. Excluded apps and embeds/links stay usable.
 */
export const isInstalledExtensionOverLimit = (
  appId: string | undefined,
  plan: StudioPlanId | null,
  countableApps: number,
  includedApps: number,
  excludedAppIds: string[] = [],
): boolean =>
  Boolean(appId) &&
  !excludedAppIds.includes(appId ?? '') &&
  isFreemiumOverAppLimit(plan, countableApps, includedApps);

/**
 * The plan the paywall's picker opens on. Free is pitched Pro rather than the
 * cheapest paid plan, as the design preselects it.
 */
export const DEFAULT_UPGRADE_PLAN: SellableStudioPlanId = 'professional';

/**
 * What a paid plan includes, quoted in the Free paywall's pitch. The one
 * allowance the server reports is the plan the workspace is already on, and the
 * pitch is for a plan it is not on yet — so this number has no read behind it
 * and mirrors the backend's `StudioIncludedApps`.
 */
export const PAID_PLAN_INCLUDED_APPS = 5;

/**
 * The interval the combined paywall quotes, matching the design. Annual is where
 * the per-app fee is cheapest relative to the plan, so the bundle reads best.
 */
export const COMBINED_DEFAULT_INTERVAL: StudioBillingInterval = 'year';

const MONTHS_PER_YEAR = 12;

/** What the combined paywall quotes: per-month lines and the period totals. */
export interface CombinedAppLimitQuote {
  planMonthlyCents: number;
  /** The fee for one app, which the copy quotes as the per-app rate. */
  appMonthlyCents: number;
  /** How many apps the checkout sells, normally one. */
  extraApps: number;
  /** Charged at checkout: one full period of the plan plus the app. */
  dueTodayCents: number;
  /** The same total again at the next renewal. */
  renewalCents: number;
}

/**
 * Prices the plan plus the extra apps for an interval. Checkout creates a new
 * subscription, so nothing prorates: today's charge is one full period, and the
 * renewal repeats it. Undefined until the catalog carries both prices.
 */
export const getCombinedQuote = (
  tier: StudioPlanTier | undefined,
  extraApp: ExtraAppPrice | undefined,
  interval: StudioBillingInterval,
  extraApps: number,
): CombinedAppLimitQuote | undefined => {
  if (!tier || !extraApp) {
    return undefined;
  }
  const isAnnual = interval === 'year' && tier.annualCents !== undefined;
  const planPeriodCents = isAnnual
    ? (tier.annualCents ?? 0)
    : tier.monthlyCents;
  const appPeriodCents = isAnnual
    ? extraApp.annualCents
    : extraApp.monthlyCents;
  const totalCents = planPeriodCents + appPeriodCents * extraApps;
  return {
    planMonthlyCents: isAnnual
      ? planPeriodCents / MONTHS_PER_YEAR
      : planPeriodCents,
    appMonthlyCents: isAnnual
      ? appPeriodCents / MONTHS_PER_YEAR
      : appPeriodCents,
    extraApps,
    dueTodayCents: totalCents,
    renewalCents: totalCents,
  };
};

/** "6th", "21st" — the app the combined paywall is about to add. */
export const toOrdinal = (value: number): string => {
  const suffixes: Record<Intl.LDMLPluralRule, string> = {
    zero: 'th',
    one: 'st',
    two: 'nd',
    few: 'rd',
    many: 'th',
    other: 'th',
  };
  const rule = new Intl.PluralRules('en-US', { type: 'ordinal' }).select(value);
  return `${value}${suffixes[rule]}`;
};

/** A paid plan the paywall can sell, with the tier its price comes from. */
export interface AppLimitPlanOption {
  planId: SellableStudioPlanId;
  tier: StudioPlanTier;
}

/**
 * The plans the paywall's picker offers, cheapest first. Derived from the
 * catalog rather than a list of its own, so the picker can only ever offer a
 * plan Stripe actually sells at a price it actually quoted.
 */
export const getUpgradePlanOptions = (
  plans: StudioPlan[] | undefined,
): AppLimitPlanOption[] =>
  (plans ?? [])
    .flatMap((plan) => {
      const tier = plan.tiers.at(0);
      // Free is what the workspace is already on, and a plan the catalog
      // carries no tier for has no price to show. Enterprise never comes
      // back from the catalog; the check is what narrows to the sellable set.
      if (plan.plan === 'freemium' || plan.plan === 'enterprise' || !tier) {
        return [];
      }
      return [{ planId: plan.plan, tier }];
    })
    .sort((a, b) => getStudioPlanRank(a.planId) - getStudioPlanRank(b.planId));

/**
 * Marks a checkout return URL with the action the combined paywall held back,
 * since the redirect to Stripe drops everything in memory. Only publish
 * resumes today; adds route through a setup page that gates again.
 */
const RESUME_PARAM = 'resumeAppLimit';

export type AppLimitResumeAction = 'publish';

export const withAppLimitResume = (
  url: string,
  action: AppLimitResumeAction,
): string => {
  const marked = new URL(url);
  marked.searchParams.set(RESUME_PARAM, action);
  return marked.toString();
};

export const readAppLimitResume = (
  search: string,
): AppLimitResumeAction | undefined =>
  new URLSearchParams(search).get(RESUME_PARAM) === 'publish'
    ? 'publish'
    : undefined;

const RESUME_PAYLOAD_KEY_PREFIX = 'appLimitResume:';

/**
 * Keeps what the held-back action needs across the checkout redirect, such as
 * the name and audience picked in the publish form, which would otherwise be
 * lost with the page. Session storage, because the redirect returns to the same
 * tab. Best-effort: where storage is unavailable the resume falls back to the
 * app's saved settings.
 *
 * An undefined payload clears the key, so every held-back attempt replaces the
 * last one — a cancelled attempt's settings can never ride along on a later
 * checkout that carried none.
 */
export const stashAppLimitResumePayload = (key: string, payload: unknown) => {
  try {
    const storageKey = `${RESUME_PAYLOAD_KEY_PREFIX}${key}`;
    if (payload === undefined) {
      window.sessionStorage.removeItem(storageKey);
      return;
    }
    window.sessionStorage.setItem(storageKey, JSON.stringify(payload));
  } catch {
    // Storage blocked or full; the resume runs without the payload.
  }
};

/** Reads and clears a stashed payload, so a later resume never reuses it. */
export const takeAppLimitResumePayload = <T>(key: string): T | undefined => {
  try {
    const storageKey = `${RESUME_PAYLOAD_KEY_PREFIX}${key}`;
    const raw = window.sessionStorage.getItem(storageKey);
    window.sessionStorage.removeItem(storageKey);
    return raw ? (JSON.parse(raw) as T) : undefined;
  } catch {
    return undefined;
  }
};

/** The search string with the marker removed, so a refresh does not resume twice. */
export const stripAppLimitResume = (search: string): string => {
  const params = new URLSearchParams(search);
  params.delete(RESUME_PARAM);
  return params.toString();
};
